'use client';

import { useId } from 'react';

import {
  ArrowRight,
  HelpCircle,
  Lightbulb,
  MessageCircleWarning,
  type LucideIcon,
} from 'lucide-react';

import type { getPostHogSupportCopy } from '@/i18n/posthog-support-copy';
import { cn } from '@/lib/utils';

import {
  getPostHogSupportContentWidth,
  getPostHogSupportHorizontalPadding,
} from './posthog-support-layout';
import type { SupportIntent } from './posthog-support-state';

type SupportCopy = ReturnType<typeof getPostHogSupportCopy>;

type IntentOption = {
  description: string;
  icon: LucideIcon;
  intent: SupportIntent;
  label: string;
};

function getIntentOptions(copy: SupportCopy): IntentOption[] {
  return [
    {
      intent: 'problem',
      label: copy.reportProblem,
      description: copy.reportProblemDescription,
      icon: MessageCircleWarning,
    },
    {
      intent: 'question',
      label: copy.askTeam,
      description: copy.askTeamDescription,
      icon: HelpCircle,
    },
    {
      intent: 'idea',
      label: copy.shareIdea,
      description: copy.shareIdeaDescription,
      icon: Lightbulb,
    },
  ];
}

export function PostHogSupportIntentChooser({
  compact,
  copy,
  onChoose,
}: {
  compact: boolean;
  copy: SupportCopy;
  onChoose: (intent: SupportIntent) => void;
}) {
  const descriptionIdPrefix = useId();
  const headingId = useId();

  return (
    <section
      aria-labelledby={headingId}
      className={cn(
        'min-h-0 flex-1 overflow-y-auto',
        getPostHogSupportHorizontalPadding(compact),
        compact ? 'py-3' : 'py-6 sm:py-10'
      )}
    >
      <div className={getPostHogSupportContentWidth(compact)}>
        <h2
          id={headingId}
          className="sr-only"
          data-support-view-heading
          tabIndex={-1}
        >
          {copy.newRequest}
        </h2>
        <div
          role="group"
          aria-labelledby={headingId}
          className={cn(
            compact
              ? 'divide-border/35 border-border/35 divide-y overflow-hidden rounded-xl border'
              : 'grid gap-3 md:grid-cols-3'
          )}
        >
          {getIntentOptions(copy).map((option) => {
            const Icon = option.icon;
            const descriptionId = `${descriptionIdPrefix}-${option.intent}`;
            return (
              <button
                key={option.intent}
                type="button"
                aria-describedby={descriptionId}
                className={cn(
                  'hover:bg-muted/45 focus-visible:bg-muted/45 focus-visible:ring-ring group relative flex w-full gap-3 text-start outline-none focus-visible:ring-2 focus-visible:ring-inset',
                  compact
                    ? 'min-h-20 items-center px-4 py-3'
                    : 'border-border/35 bg-card min-h-28 items-start rounded-xl border p-4 pe-11 sm:min-h-36 sm:flex-col sm:p-5'
                )}
                onClick={() => onChoose(option.intent)}
              >
                <span
                  className={cn(
                    'bg-muted text-muted-foreground flex shrink-0 items-center justify-center rounded-lg',
                    compact ? 'size-9' : 'size-10'
                  )}
                >
                  <Icon className="size-4" strokeWidth={1.75} aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span
                    className={cn(
                      'block font-medium text-balance',
                      compact ? 'text-sm' : 'text-base'
                    )}
                  >
                    {option.label}
                  </span>
                  <span
                    id={descriptionId}
                    className="text-muted-foreground mt-1 block text-xs leading-5 text-pretty sm:text-sm"
                  >
                    {option.description}
                  </span>
                </span>
                <ArrowRight
                  className={cn(
                    'text-muted-foreground size-4 shrink-0 rtl:rotate-180',
                    !compact && 'absolute end-4 top-5'
                  )}
                  aria-hidden
                />
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
